import {
  Project,
  ProjectAddress,
  ProjectAddressMapPosition,
  ProjectInfoStatistics,
  TypedElement,
} from '../entities/project'
import {daysOfWeek, ProjectWeather, weatherCodeRegistry} from '../entities/weather'
import {supabase} from './BackendService'
import {instance as toDoService} from '../services/ToDoService'
import {markProjectForRevalidation} from '../routes/projectRevalidator'
import {MapImage} from '../components/generalProjectInfoForm/EditorProjectMap'
import useImage from 'use-image'
import {dataURItoBlob} from '../util/dataURIToBlob'
import {cached, cachedById} from './util'
import {instance as plantsService} from '../services/PlantsService'

function generateProjectMapFileName(userId: string, projectId: string) {
  return `${userId}/projectMap-${projectId}.jpg`
}

export class ProjectsService {
  public getUserProjects = cached<Project[]>(async () => {
    let {data: projects, error} = await supabase
      .from('projects')
      .select('*')
      .eq('ownerId', (await supabase.auth.getSession()).data.session?.user.id)
      .eq('isSnapshot', false)

    return projects as Project[]
  })

  public async getProjectLocation(lon: string, lat: string): Promise<string> {
    const locationAPICall = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}`

    let locationName: string
    try {
      const response = await fetch(locationAPICall)
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const data = await response.json()
      locationName = `${data.city},${data.locality}`
    } catch (error) {
      console.error('Error:', error)
    }

    return locationName
  }

  public async searchLocation(query: string) {
    let locationNames: ProjectAddress[]

    const locationAPICall = `https://geocode.maps.co/search?q=${query}&api_key=66431a03acd0e847129730tba878cce`

    try {
      const response = await fetch(locationAPICall)
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const data = await response.json()
      locationNames = data.map((a) => ({
        addressName: a.display_name,
        latitude: a.lat,
        longtitude: a.lon,
      }))
    } catch (error) {
      console.error('Error:', error)
    }

    return locationNames
  }

  public async getUserLocation() {
    let location: ProjectAddress

    const locationAPICall = `https://free.freeipapi.com/api/json`

    try {
      const response = await fetch(locationAPICall)
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const data = await response.json()
      location = {
        addressName: `${data.countryName}, ${data.regionName}, ${data.cityName}`,
        latitude: data.latitude,
        longtitude: data.longitude,
      }
    } catch (error) {
      console.error('Error:', error)
    }

    return location
  }

  public async getProjectWeather(lon: string, lat: string): Promise<ProjectWeather> {
    const weatherAPICall = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,is_day,weather_code&hourly=temperature_2m&daily=weather_code,temperature_2m_max,temperature_2m_min&wind_speed_unit=ms`

    let weather: ProjectWeather

    try {
      const response = await fetch(weatherAPICall)
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const data = await response.json()
      const currentDate = new Date()
      weather = {
        ...weather,
        currentTempreture: data.current.temperature_2m,
        icon: weatherCodeRegistry[data.current.weather_code].weatherIcon,
        description: weatherCodeRegistry[data.current.weather_code].descriptionShort,
        nextDaysWeather: [],
        locationName: await this.getProjectLocation(lon, lat),
        date: currentDate.toLocaleDateString(),
        backImage: weatherCodeRegistry[data.current.weather_code].imageBack,
      }
      for (let i = 0; i < 7; i++) {
        weather.nextDaysWeather.push({
          dayOfWeek:
            daysOfWeek[
              currentDate.getDay() + i > 6 ? currentDate.getDay() + i - 7 : currentDate.getDay() + i
            ],
          icon: weatherCodeRegistry[data.daily.weather_code[i]].weatherIcon,
          dayTemp: data.daily.temperature_2m_max[i],
          nightTemp: data.daily.temperature_2m_min[i],
        })
      }
    } catch (error) {
      console.error('Error:', error)
    }

    return weather
  }

  public getUserProjectsInfoStatistics = cached(async () => {
    let {data: projects, error} = await supabase
      .from('projects')
      .select(
        'id, name, photo, location, plantedPlantsAmount:planted_plants_data(count), planted_plants_data(projectId, overwrittenBaseFields, basePlant:plantBaseId( id, fields, image_id ))'
      )
      .eq('ownerId', (await supabase.auth.getSession()).data.session?.user.id)
      .eq('isSnapshot', false)

    const projectsStatistics = projects.map((project) => {
      let projectPlantTypes: Record<string, number> = {}
      let projectPlantAttractivenessFactors: Record<string, number> = {}

      for (const plant of project.planted_plants_data as any[]) {
        let plantTypes = [] as (typeof plant.overwrittenBaseFields)['Тип']

        if (plant.overwrittenBaseFields['Тип']) {
          plantTypes = plant.overwrittenBaseFields['Тип']
        } else if (plant.basePlant?.fields['Тип']) {
          plantTypes = plant.basePlant?.fields['Тип']
        }

        for (const type of plantTypes) {
          projectPlantTypes[type] ? projectPlantTypes[type]++ : (projectPlantTypes[type] = 1)
        }

        const plantAttractivenessFactors =
          (plant.overwrittenBaseFields['Факторы привлекательности']
            ? plant.overwrittenBaseFields['Факторы привлекательности']
            : plant.basePlant?.fields['Факторы привлекательности']) || []

        for (const factor of plantAttractivenessFactors) {
          projectPlantAttractivenessFactors[factor]
            ? projectPlantAttractivenessFactors[factor]++
            : (projectPlantAttractivenessFactors[factor] = 1)
        }
      }

      return {
        id: project.id,
        name: project.name,
        plantedPlantsAmount: (project.plantedPlantsAmount as {count: number}[])[0].count,
        plantedPlantsTypes: projectPlantTypes,
        plantedPlantsAttractivenessFactors: projectPlantAttractivenessFactors,
        photo: project.photo,
        coordinates: project.location
          ? [project.location.longtitude, project.location.latitude]
          : null,
      }
    })

    return projectsStatistics as ProjectInfoStatistics[]
  })

  public getUserProjectMaxPlantsInfoStatistics = cached(async () => {
    const projectsStatistics = await this.getUserProjectsInfoStatistics()

    if (projectsStatistics.length === 0) {
      return null
    }

    let max = -1
    let index = 0

    projectsStatistics.forEach(function (v, k) {
      if (max < v.plantedPlantsAmount) {
        max = v.plantedPlantsAmount
        index = k
      }
    })

    if (projectsStatistics[index].coordinates) {
      projectsStatistics[index].weather = await this.getProjectWeather(
        projectsStatistics[index].coordinates[0],
        projectsStatistics[index].coordinates[1]
      )
    } else {
      projectsStatistics[index].weather = null
    }

    // projectsStatistics[index].mainToDos = await toDoService.getUserUnfinishedToDos(3, {
    //   projectId: projectsStatistics[index].id,
    // })

    return projectsStatistics[index] as ProjectInfoStatistics
  })

  public getUserProjectElements = cachedById(async (id: string) => {
    const project = await this.getUserProjectById(id)
    return project.elements || []
  })

  public getUserProjectsNames = cached(async () => {
    let {data: projects, error} = await supabase
      .from('projects')
      .select('id, name')
      .eq('ownerId', (await supabase.auth.getSession()).data.session?.user.id)
      .eq('isSnapshot', false)

    return projects as {id: string; name: string}[]
  })

  public getUserProjectById = cachedById(async (id: string) => {
    let {data: project, error} = await supabase
      .from('projects')
      .select('*')
      .eq('id', id)
      .maybeSingle()

    return project as any as Project
  })

  public async uploadProjectMap(
    projectId: string,
    mapImage: MapImage
  ): Promise<{success: true} | {success: false; error: any}> {
    const userId = (await supabase.auth.getSession()).data.session?.user.id

    const name = generateProjectMapFileName(userId, projectId)

    const {data, error} = await supabase.storage
      .from('photos')
      .upload(name, dataURItoBlob(mapImage.srcUrl), {
        cacheControl: '3600',
        upsert: true,
      })

    if (error) {
      return {success: false, error}
    } else {
      return {success: true}
    }
  }

  public getProjectMapUrl = cachedById<
    {success: true; url: string} | {success: false; error: any},
    string
  >(async (projectId: string) => {
    const userId = (await supabase.auth.getSession()).data.session?.user.id

    const name = generateProjectMapFileName(userId, projectId)

    const {data, error} = await supabase.storage.from('photos').createSignedUrl(name, 60)

    if (error) {
      return {success: false, error: error}
    } else {
      return {success: true, url: data.signedUrl}
    }
  })

  public getProjectMapAttributes = cachedById<ProjectAddressMapPosition | null, string>(
    async (id: string) => {
      let {data: location, error} = await supabase
        .from('projects')
        .select('location')
        .eq('id', id)
        .maybeSingle()

      return (location.location.map as ProjectAddressMapPosition) || null
    }
  )

  private clearProjectListCaches() {
    this.getUserProjects.clearCache()
    this.getUserProjectsInfoStatistics.clearCache()
    this.getUserProjectsNames.clearCache()
    this.getUserProjectMaxPlantsInfoStatistics.clearCache()
    this.getUserProjectsInfoStatistics.clearCache()
    plantsService.getPlantById.clearAllCached()
  }

  public async createProject(
    newProject: Omit<Project, 'id'>,
    mapImage?: MapImage
  ): Promise<{success: true; id: number} | {success: false; error: any}> {
    const {data, error} = await supabase
      .from('projects')
      .insert([newProject])
      .select('id')
      .maybeSingle()

    if (error) {
      return {success: false, error}
    }

    const projectId = data.id

    if (mapImage) {
      const result = await this.uploadProjectMap(projectId, mapImage)
      if (result.success === false) {
        return result
      }
    }

    this.clearProjectListCaches()
    return {success: true, id: projectId}
  }

  public async editProject(
    currentProject: Project,
    mapImage?: MapImage
  ): Promise<{success: true; id: number} | {success: false; error: any}> {
    if (mapImage) {
      const result = await this.uploadProjectMap(currentProject.id, mapImage)
      if (result.success === false) {
        return result
      }
    }

    const {data, error} = await supabase
      .from('projects')
      .update([currentProject])
      .eq('id', currentProject.id)
      .select('id')
      .maybeSingle()

    if (error) {
      return {success: false, error}
    } else {
      markProjectForRevalidation()
      this.clearProjectListCaches()
      this.getUserProjectById.clearCachedById(currentProject.id)
      this.getUserProjectElements.clearCachedById(currentProject.id)
      return {success: true, id: data.id}
    }
  }

  public async deleteProject(
    currentProjectId: string,
    isAlsoRemovePlants: boolean,
    isAlsoRemovePlantsEvents: boolean
  ): Promise<{success: true} | {success: false; error: any}> {
    const {data, error} = await supabase.functions.invoke(`delete-project`, {
      body: {
        projectId: currentProjectId,
        alsoRemovePlants: isAlsoRemovePlants,
        alsoRemovePlantsEvents: isAlsoRemovePlantsEvents,
      },
    })

    if (error) {
      return {success: false, error}
    } else {
      this.clearProjectListCaches()
      return {success: true}
    }
  }

  public async getUserSnapshots(): Promise<Project[]> {
    let {data: projects, error} = await supabase
      .from('projects')
      .select('*')
      .eq('ownerId', (await supabase.auth.getSession()).data.session?.user.id)
      .eq('isSnapshot', true)

    return projects as Project[]
  }

  public async makeProjectSnapshot(
    currentProjectId: number
  ): Promise<{success: true} | {success: false; error: any}> {
    const {data, error} = await supabase.rpc('createProjectSnapshot', {projectid: currentProjectId})

    if (error) {
      return {success: false, error}
    } else {
      return {success: true}
    }
  }
}

export const instance = new ProjectsService()
